import { useState } from "react";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Briefcase, Clock, FileCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level motion configs (no per-render allocation)
const FADE_UP = { opacity: 0, y: 20 };
const FADE_IN_PLACE = { opacity: 1, y: 0 };
const SCALE_IN = { opacity: 0, scale: 0.95 };
const SCALE_REST = { opacity: 1, scale: 1 };
const TRANSITION_DEFAULT = { duration: 0.6 };

const RecruitmentPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    // Section 1: Personal Information
    firstName: "",
    surname: "",
    homeTelephone: "",
    mobileTelephone: "",
    email: "",
    homeAddress: "",
    postCode: "",
    
    // Section 2: Employment Information
    position: "cleaner",
    experienceYears: "",
    experienceMonths: "",
    hasDBSCertificate: "",
    previousCompanies: "",
    
    // Availability
    availability: {
      monday: { available: false, from: "", to: "" },
      tuesday: { available: false, from: "", to: "" },
      wednesday: { available: false, from: "", to: "" },
      thursday: { available: false, from: "", to: "" },
      friday: { available: false, from: "", to: "" },
      saturday: { available: false, from: "", to: "" },
      sunday: { available: false, from: "", to: "" }
    },
    
    // Section 3: Additional Information
    additionalInfo: "",
    
    // Section 4: Declaration
    legallyEntitled: false,
    hasValidID: false,
    hasEnglishKnowledge: false,
    confirmCorrect: false
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAvailabilityChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate declarations
    if (!formData.legallyEntitled || !formData.hasValidID || !formData.hasEnglishKnowledge || !formData.confirmCorrect) {
      toast.error("Please agree to all declarations before submitting");
      return;
    }
    
    // Validate required fields
    if (!formData.firstName || !formData.surname || !formData.email || !formData.mobileTelephone) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/recruitment/apply`, formData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const timeOptions = [
    "06:00", "06:30", "07:00", "07:30", "08:00", "08:30", "09:00", "09:30",
    "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30",
    "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
    "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00"
  ];

  const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  if (submitted) {
    return (
      <Layout>
        <section className="py-24 md:py-32">
          <div className="container-clean max-w-2xl">
            <motion.div
              initial={SCALE_IN}
              animate={SCALE_REST}
              className="text-center"
            >
              <div className="w-20 h-20 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#2D5016]" />
              </div>
              <h1 className="text-4xl font-bold text-[#1A2E12] mb-4">Application Submitted!</h1>
              <p className="text-lg text-[#5C6B57] mb-8">
                Thank you for your interest in joining Clean Kin. We will review your application 
                and contact you within 5 working days.
              </p>
              <Button 
                onClick={() => window.location.href = '/'}
                className="bg-[#2D5016] hover:bg-[#1A2E12] rounded-full px-8"
              >
                Return to Home
              </Button>
            </motion.div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[#F9FAF9]">
        <div className="container-clean">
          <motion.div
            initial={FADE_UP}
            animate={FADE_IN_PLACE}
            transition={TRANSITION_DEFAULT}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              Join Our Team
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A2E12] tracking-tight mb-6" data-testid="recruitment-title">
              Work With Clean Kin
            </h1>
            <p className="text-lg text-[#5C6B57] leading-relaxed">
              We're looking for dedicated individuals to join our professional cleaning team.
              Complete the application form below to be considered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16">
        <div className="container-clean max-w-3xl">
          <form onSubmit={handleSubmit}>
            {/* Section 1: Personal Information */}
            <Card className="bg-white border-[#E2E8E0] rounded-2xl mb-8">
              <CardHeader className="border-b border-[#E2E8E0]">
                <CardTitle className="flex items-center gap-3 text-[#1A2E12]">
                  <Users className="w-5 h-5 text-[#2D5016]" />
                  Section 1: Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">First Name *</Label>
                    <Input
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                      className="mt-1"
                      required
                      data-testid="recruitment-firstname"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Surname *</Label>
                    <Input
                      name="surname"
                      value={formData.surname}
                      onChange={handleInputChange}
                      placeholder="Enter your surname"
                      className="mt-1"
                      required
                      data-testid="recruitment-surname"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Home Telephone</Label>
                    <Input
                      name="homeTelephone"
                      value={formData.homeTelephone}
                      onChange={handleInputChange}
                      placeholder="e.g. 020 1234 5678"
                      className="mt-1"
                      data-testid="recruitment-home-tel"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-[#2D5016]">Mobile Telephone Number *</Label>
                    <Input
                      name="mobileTelephone"
                      value={formData.mobileTelephone}
                      onChange={handleInputChange}
                      placeholder="e.g. 07700 900000"
                      className="mt-1"
                      required
                      data-testid="recruitment-mobile"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#2D5016]">Email *</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    className="mt-1"
                    required
                    data-testid="recruitment-email"
                  />
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#2D5016]">Home Address *</Label>
                  <Textarea
                    name="homeAddress"
                    value={formData.homeAddress}
                    onChange={handleInputChange}
                    placeholder="Enter your full home address"
                    className="mt-1"
                    rows={3}
                    required
                    data-testid="recruitment-address"
                  />
                </div>

                <div className="max-w-xs">
                  <Label className="text-sm font-semibold text-[#2D5016]">Post Code *</Label>
                  <Input
                    name="postCode"
                    value={formData.postCode}
                    onChange={handleInputChange}
                    placeholder="e.g. SW1A 1AA"
                    className="mt-1"
                    required
                    data-testid="recruitment-postcode"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Employment Information */}
            <Card className="bg-white border-[#E2E8E0] rounded-2xl mb-8">
              <CardHeader className="border-b border-[#E2E8E0]">
                <CardTitle className="flex items-center gap-3 text-[#1A2E12]">
                  <Briefcase className="w-5 h-5 text-[#2D5016]" />
                  Section 2: Employment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div>
                  <Label className="text-sm font-semibold text-[#2D5016] mb-3 block">Position Applied For</Label>
                  <div className="bg-[#E8F5E9] rounded-xl p-4 inline-block">
                    <span className="font-semibold text-[#2D5016]">A Cleaner Position</span>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#2D5016] mb-3 block">Experience in Cleaning</Label>
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <Label className="text-xs text-[#5C6B57]">Years</Label>
                      <Select 
                        value={formData.experienceYears} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, experienceYears: value }))}
                      >
                        <SelectTrigger data-testid="recruitment-years">
                          <SelectValue placeholder="Years" />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(31)].map((_, i) => (
                            <SelectItem key={`years-${i}`} value={String(i)}>{i}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C6B57]">Months</Label>
                      <Select 
                        value={formData.experienceMonths} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, experienceMonths: value }))}
                      >
                        <SelectTrigger data-testid="recruitment-months">
                          <SelectValue placeholder="Months" />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(12)].map((_, i) => (
                            <SelectItem key={`months-${i}`} value={String(i)}>{i}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#2D5016] mb-3 block">
                    Do you have a current valid DBS certificate? *
                  </Label>
                  <RadioGroup
                    value={formData.hasDBSCertificate}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, hasDBSCertificate: value }))}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="dbs-yes" data-testid="recruitment-dbs-yes" />
                      <Label htmlFor="dbs-yes" className="cursor-pointer">Yes</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="dbs-no" data-testid="recruitment-dbs-no" />
                      <Label htmlFor="dbs-no" className="cursor-pointer">No</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-[#2D5016]">What companies have you worked for previously?</Label>
                  <Textarea
                    name="previousCompanies"
                    value={formData.previousCompanies}
                    onChange={handleInputChange}
                    placeholder="List previous employers, including company names and your role"
                    className="mt-1"
                    rows={4}
                    data-testid="recruitment-previous"
                  />
                </div>

                {/* Availability */}
                <div>
                  <Label className="text-sm font-semibold text-[#2D5016] mb-4 block">What is your availability?</Label>
                  <div className="bg-[#F9FAF9] rounded-xl p-4">
                    <div className="grid gap-3">
                      {days.map((day) => (
                        <div key={day} className="flex items-center gap-4 py-2 border-b border-[#E2E8E0] last:border-0">
                          <div className="w-28 flex items-center gap-2">
                            <Checkbox
                              id={`avail-${day}`}
                              checked={formData.availability[day].available}
                              onCheckedChange={(checked) => handleAvailabilityChange(day, 'available', checked)}
                              data-testid={`recruitment-avail-${day}`}
                            />
                            <Label htmlFor={`avail-${day}`} className="capitalize cursor-pointer font-medium text-[#1A2E12]">
                              {day}
                            </Label>
                          </div>
                          
                          {formData.availability[day].available && (
                            <div className="flex items-center gap-2 flex-1">
                              <Label className="text-xs text-[#5C6B57] whitespace-nowrap">From:</Label>
                              <Select
                                value={formData.availability[day].from}
                                onValueChange={(value) => handleAvailabilityChange(day, 'from', value)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue placeholder="Time" />
                                </SelectTrigger>
                                <SelectContent>
                                  {timeOptions.map((time) => (
                                    <SelectItem key={time} value={time}>{time}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              
                              <Label className="text-xs text-[#5C6B57] whitespace-nowrap">To:</Label>
                              <Select
                                value={formData.availability[day].to}
                                onValueChange={(value) => handleAvailabilityChange(day, 'to', value)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue placeholder="Time" />
                                </SelectTrigger>
                                <SelectContent>
                                  {timeOptions.map((time) => (
                                    <SelectItem key={time} value={time}>{time}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 3: Additional Information */}
            <Card className="bg-white border-[#E2E8E0] rounded-2xl mb-8">
              <CardHeader className="border-b border-[#E2E8E0]">
                <CardTitle className="flex items-center gap-3 text-[#1A2E12]">
                  <Clock className="w-5 h-5 text-[#2D5016]" />
                  Section 3: Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <Label className="text-sm font-semibold text-[#2D5016]">
                  Please feel free to add any additional information to include on this application to be considered for the role.
                </Label>
                <Textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  placeholder="Any additional skills, qualifications, or information you'd like us to know..."
                  className="mt-2"
                  rows={5}
                  data-testid="recruitment-additional"
                />
              </CardContent>
            </Card>

            {/* Section 4: Declaration */}
            <Card className="bg-white border-[#E2E8E0] rounded-2xl mb-8">
              <CardHeader className="border-b border-[#E2E8E0]">
                <CardTitle className="flex items-center gap-3 text-[#1A2E12]">
                  <FileCheck className="w-5 h-5 text-[#2D5016]" />
                  Section 4: Declaration
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm text-[#5C6B57] mb-6">
                  Please only tick the box if you have read, understood and agreed with the statement:
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-4 bg-[#F9FAF9] rounded-xl">
                    <Checkbox
                      id="legally-entitled"
                      checked={formData.legallyEntitled}
                      onCheckedChange={(checked) => handleCheckboxChange('legallyEntitled', checked)}
                      className="mt-0.5"
                      data-testid="recruitment-legal"
                    />
                    <Label htmlFor="legally-entitled" className="text-sm text-[#1A2E12] cursor-pointer leading-relaxed">
                      I am legally entitled to live and work in the UK
                    </Label>
                  </div>

                  <div className="flex items-start space-x-3 p-4 bg-[#F9FAF9] rounded-xl">
                    <Checkbox
                      id="valid-id"
                      checked={formData.hasValidID}
                      onCheckedChange={(checked) => handleCheckboxChange('hasValidID', checked)}
                      className="mt-0.5"
                      data-testid="recruitment-id"
                    />
                    <Label htmlFor="valid-id" className="text-sm text-[#1A2E12] cursor-pointer leading-relaxed">
                      I have valid proof of ID (passport or identity card), proof of address and recent bank statement 
                      (issued within the last three months) that I can bring to my interview
                    </Label>
                  </div>

                  <div className="flex items-start space-x-3 p-4 bg-[#F9FAF9] rounded-xl">
                    <Checkbox
                      id="english-knowledge"
                      checked={formData.hasEnglishKnowledge}
                      onCheckedChange={(checked) => handleCheckboxChange('hasEnglishKnowledge', checked)}
                      className="mt-0.5"
                      data-testid="recruitment-english"
                    />
                    <Label htmlFor="english-knowledge" className="text-sm text-[#1A2E12] cursor-pointer leading-relaxed">
                      I have basic English language knowledge and can communicate without difficulty
                    </Label>
                  </div>

                  <div className="flex items-start space-x-3 p-4 bg-[#F9FAF9] rounded-xl">
                    <Checkbox
                      id="confirm-correct"
                      checked={formData.confirmCorrect}
                      onCheckedChange={(checked) => handleCheckboxChange('confirmCorrect', checked)}
                      className="mt-0.5"
                      data-testid="recruitment-confirm"
                    />
                    <Label htmlFor="confirm-correct" className="text-sm text-[#1A2E12] cursor-pointer leading-relaxed">
                      I confirm that the information given above is correct
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-12 py-6 text-lg"
                disabled={loading}
                data-testid="recruitment-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Application"
                )}
              </Button>
              <p className="text-sm text-[#5C6B57] mt-4">
                By submitting this application, you agree to our Privacy Policy and Terms of Service.
              </p>
            </div>
          </form>
        </div>
      </section>
    </Layout>
  );
};

export default RecruitmentPage;
